import { useState } from 'react';
import { Target } from 'lucide-react';
import { db } from '@/lib/db';
import type { CallYourShot, Course, Member, MemberPublic } from '@/lib/db/types';
import { getCourseCampus, getCourseComboLabel } from '@/lib/courses/group';
import { timeAgo } from '@/lib/timeAgo';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  shot: CallYourShot;
  member: Member;
  caller: MemberPublic | { name: string };
  course?: Course | null;
  reacted: boolean;
  staggerIndex?: number;
  onCallYours?: () => void;
  onOpenProfile?: (id: string) => void;
  onReacted?: () => void;
}

export function CallYourShotTimelineCard({
  shot,
  member,
  caller,
  course,
  reacted,
  staggerIndex = 0,
  onCallYours,
  onOpenProfile,
  onReacted,
}: Props) {
  const [busy, setBusy] = useState(false);
  const isMine = shot.player_id === member.id;

  const handleReact = async (type: 'eyes' | 'fire' | 'target') => {
    if (isMine || reacted || busy) return;
    setBusy(true);
    try {
      await db.callShots.react(shot.id, type);
      onReacted?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="fade-up rounded-2xl border-2 border-gold bg-paper shadow-bt"
      style={{ animationDelay: `${staggerIndex * 50}ms` }}
    >
      <div className="flex items-center gap-2.5 border-b border-gold/30 bg-gradient-to-r from-gold/15 to-paper px-4 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold text-navy">
          <Target size={16} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-extrabold tracking-[0.18em] text-gold-deep">CALL YOUR SHOT</div>
          <div className="font-serif text-sm font-bold text-ink">Live target on the line</div>
        </div>
        <div className="text-[10px] font-semibold text-mute">{timeAgo(shot.proposed_at)} ago</div>
      </div>

      <div className="px-4 py-3.5">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => onOpenProfile?.(shot.player_id)}
            className="shrink-0"
          >
            <Avatar member={caller} size={40} />
          </button>
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => onOpenProfile?.(shot.player_id)}
              className="text-left font-serif text-base font-bold text-ink"
            >
              {caller.name}
              {isMine && <span className="text-gold-deep"> · You</span>}
            </button>
            {course && (
              <div className="mt-0.5 text-[11px] text-mute">
                {getCourseCampus(course)} · {getCourseComboLabel(course)}
              </div>
            )}
            {shot.notes && (
              <div className="mt-1.5 text-xs italic leading-snug text-ink-soft">
                &ldquo;{shot.notes}&rdquo;
              </div>
            )}
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[9px] font-bold tracking-wide text-mute">TARGET</div>
            <div className="font-serif text-3xl font-bold leading-none text-navy">{shot.target_score}</div>
          </div>
        </div>

        {!isMine && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {(['eyes', 'fire', 'target'] as const).map((type) => (
              <button
                key={type}
                type="button"
                disabled={reacted || busy}
                onClick={() => void handleReact(type)}
                className="rounded-full border border-line bg-cream px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
              >
                {type === 'eyes' ? '👀 Watching' : type === 'fire' ? '🔥 Let\'s go' : '🎯 Called it'}
              </button>
            ))}
            {reacted && <span className="text-[10px] font-semibold text-good">You reacted</span>}
          </div>
        )}

        {isMine && (
          <p className="mt-3 text-[11px] leading-relaxed text-mute">
            Your call is live. Post your round when you finish to auto-settle, or settle from Call Your Shot.
          </p>
        )}

        {!isMine && onCallYours && (
          <button
            type="button"
            onClick={onCallYours}
            className="mt-3 w-full rounded-lg border border-dashed border-navy/30 py-2 text-xs font-bold text-navy"
          >
            Call yours
          </button>
        )}
      </div>
    </article>
  );
}
